// Browser preview entrypoint (main.tsx) works like a standard React DOM app

import { getBrowserDocument, isBrowserCanvas } from "./dom";
import { installNxJsPolyfills } from "./polyfills";

async function bootstrap(): Promise<void> {
  const doc = getBrowserDocument();
  if (!doc) {
    throw new Error("Expected a browser document (browser preview only)");
  }
  const canvas = doc.getElementById("screen");
  if (!isBrowserCanvas(canvas)) {
    throw new Error("Expected a <canvas id='screen'> element in index.html");
  }

  await installNxJsPolyfills(canvas);

  // Dynamically import everything AFTER the polyfills
  // This makes react-tela comfortable with rendering to the DOM instead of the OG runtime
  const [
    { default: React },
    { render },
    { App },
    { createWebGLTelaSurface, TelaPath2D },
  ] = await Promise.all([
    import("react"),
    import("react-tela/render"),
    import("../App"),
    import("../rendering/webgl"),
  ]);

  await loadFonts();

  const surface = createWebGLTelaSurface(screen);
  const root = render(React.createElement(App), surface.canvas, {
    Path2D: TelaPath2D as unknown as NonNullable<
      Parameters<typeof render>[2]
    >["Path2D"],
  });
  const detach = surface.attach(root);
  globalThis.addEventListener(
    "beforeunload",
    () => {
      detach();
      surface.dispose();
    },
    { once: true },
  );
}

// Fetch Source Sans Pro for app
async function loadFonts(): Promise<void> {
  const fontDefs: ReadonlyArray<readonly [string, string]> = [
    ["SourceSansPro-Regular", "romfs:/fonts/SourceSansPro-Regular.otf"],
    ["SourceSansPro-Bold", "romfs:/fonts/SourceSansPro-Bold.otf"],
  ];
  await Promise.all(
    fontDefs.map(async ([family, path]) => {
      const data = await Switch.readFile(path);
      if (!data) {
        console.warn(`[browser] Failed to load font ${family} from ${path}`);
        return;
      }
      const face = new FontFace(family, data);
      await face.load();
      fonts.add(face);
    }),
  );
}

bootstrap().catch((err) => {
  console.error("[browser] bootstrap failed:", err);
});
